import {
  checkArgument,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {approxLtOrEqual} from 'external/dev_april_corgi+/js/common/comparisons';

import {MaterialId, PlateId, WellId} from './data';

export interface WellLocation {
  plate: PlateId;
  well: WellId;
}

export class WellState {
  private currentNl: number;
  private highVolumeNl: number;

  constructor(
    readonly id: WellId,
    readonly plate: PlateId,
    private readonly sandbox: boolean,
  ) {
    this.currentNl = 0;
    this.highVolumeNl = 0;
  }

  getCurrentNl(): number {
    return this.currentNl;
  }

  getHighVolumeNl(): number {
    return this.highVolumeNl;
  }

  get location(): WellLocation {
    return {plate: this.plate, well: this.id};
  }

  load(volumeNl: number): void {
    this.currentNl += volumeNl;
    // Don't add delta because this volume is hand-loaded
    this.highVolumeNl = Math.max(this.highVolumeNl, this.currentNl);
  }

  add(volumeNl: number): void {
    this.currentNl += volumeNl;
    this.highVolumeNl = Math.max(this.highVolumeNl, this.currentNl);
  }

  remove(volumeNl: number): void {
    if (!this.sandbox) {
      checkArgument(
        approxLtOrEqual(volumeNl, this.currentNl, /* epsilon= */ 1 /* nl */),
        `${volumeNl}nl exceeds current volume ${this.currentNl}nl in ${this.plate}-${this.id}`,
      );
    }
    this.currentNl -= volumeNl;
  }
}

export class PlateState {
  private readonly shared: Map<MaterialId, WellId>;
  private readonly wells: Map<WellId, WellState>;
  private lastId: number;

  constructor(
    readonly id: PlateId,
    private readonly sandbox: boolean,
    readonly maxWellVolumeNl: number,
  ) {
    this.shared = new Map();
    this.wells = new Map();
    this.lastId = -1;
  }

  getCurrentVolumeNl(): number {
    let max = 0;
    for (const well of this.wells.values()) {
      max = Math.max(max, well.getCurrentNl());
    }
    return max;
  }

  getSharedWell(id: MaterialId): WellState {
    let wellId = this.shared.get(id);
    if (!wellId) {
      wellId = this.reserveWell().id;
      this.shared.set(id, wellId);
    }
    return checkExists(this.wells.get(wellId));
  }

  reserveWell(): WellState {
    const well = new WellState(
      String(++this.lastId) as WellId,
      this.id,
      this.sandbox,
    );
    this.wells.set(well.id, well);
    return well;
  }

  well(id: WellId): WellState {
    return checkExists(this.wells.get(id));
  }
}
